import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus as PlusIcon, X as XIcon } from "lucide-react";
import type { ContactGroupSummary } from "@multi-chat/shared";
import { Badge } from "../../../components/ui/badge/Badge.js";
import { Button } from "../../../components/ui/button/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover/index.js";
import { cn } from "../../../lib/utils.js";
import { contactFormFieldInputClass } from "../contact-sheet-presentation.js";
import { contactGroupChipClassName, contactGroupChipClassNameFromKey } from "../contact-group-color.js";
import type { ContactGroupSelectionValue } from "../contact-group-selection-value.js";

export type ContactGroupMultiSelectPresentation = "default" | "section";

export function ContactGroupMultiSelect({
  groups,
  value,
  onChange,
  disabled = false,
  loading = false,
  className,
  presentation = "default",
  density = "default",
  allowCreateNewGroup = true,
  showEmptyHint = true,
  triggerClassName,
  menuClassName,
  emptyHint,
  existingLabel,
  existingTrigger,
  createSectionLabel,
  createPlaceholder,
}: {
  groups: ContactGroupSummary[];
  value: ContactGroupSelectionValue;
  onChange: (value: ContactGroupSelectionValue) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  presentation?: ContactGroupMultiSelectPresentation;
  density?: "default" | "compact" | "review";
  allowCreateNewGroup?: boolean;
  showEmptyHint?: boolean;
  triggerClassName?: string;
  menuClassName?: string;
  emptyHint?: string;
  existingLabel?: string;
  existingTrigger?: string;
  createSectionLabel?: string;
  createPlaceholder?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [newGroupDraft, setNewGroupDraft] = useState("");
  const [existingOpen, setExistingOpen] = useState(false);
  const dir = i18n.dir() === "rtl" ? "rtl" : "ltr";

  const groupNameById = useMemo(
    () => new Map(groups.map((group) => [group.id, group.name])),
    [groups],
  );

  const availableGroups = useMemo(
    () => groups.filter((group) => !value.groupIds.includes(group.id)),
    [groups, value.groupIds],
  );

  const selectedExisting = value.groupIds.map((id) => ({
    id,
    name: groupNameById.get(id) ?? t("contacts.groups.multiSelect.unknownGroup"),
  }));

  function removeExistingGroup(groupId: number): void {
    onChange({
      ...value,
      groupIds: value.groupIds.filter((id) => id !== groupId),
    });
  }

  function removeNewGroup(name: string): void {
    onChange({
      ...value,
      newGroupNames: value.newGroupNames.filter((entry) => entry !== name),
    });
  }

  function addExistingGroup(groupId: number): void {
    if (value.groupIds.includes(groupId)) return;
    onChange({
      ...value,
      groupIds: [...value.groupIds, groupId],
    });
  }

  function addNewGroup(): void {
    const trimmed = newGroupDraft.trim();
    if (trimmed.length === 0) return;

    const matchingExisting = groups.find(
      (group) => group.name.localeCompare(trimmed, undefined, { sensitivity: "accent" }) === 0,
    );
    if (matchingExisting) {
      if (!value.groupIds.includes(matchingExisting.id)) {
        onChange({
          ...value,
          groupIds: [...value.groupIds, matchingExisting.id],
        });
      }
      setNewGroupDraft("");
      return;
    }

    if (value.newGroupNames.some((name) => name === trimmed)) {
      setNewGroupDraft("");
      return;
    }

    onChange({
      ...value,
      newGroupNames: [...value.newGroupNames, trimmed],
    });
    setNewGroupDraft("");
  }

  const hasSelection =
    selectedExisting.length > 0 || (allowCreateNewGroup && value.newGroupNames.length > 0);
  const controlsDisabled = disabled || loading;
  const isSection = presentation === "section";
  const compact = density === "compact" || density === "review";
  const reviewDensity = density === "review";
  const existingLabelText = existingLabel ?? t("contacts.groups.multiSelect.addExistingLabel");
  const existingTriggerText = existingTrigger ?? t("contacts.groups.multiSelect.addExisting");
  const createSectionLabelText =
    createSectionLabel ?? t("contacts.groups.multiSelect.createSectionLabel");
  const createPlaceholderText =
    createPlaceholder ?? t("contacts.groups.multiSelect.createPlaceholder");
  const selectClassName = cn(
    "flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border bg-background text-start text-foreground transition-[border-color,box-shadow] hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color-mix(in_srgb,var(--color-accent)_55%,transparent)] disabled:cursor-not-allowed disabled:opacity-70",
    reviewDensity ? "h-8 px-2 text-[0.75rem]" : "h-9 px-2.5 text-[0.8125rem]",
    isSection && !reviewDensity ? contactFormFieldInputClass : null,
    triggerClassName,
  );
  const createInputClassName = cn(
    "min-w-0 flex-1 disabled:opacity-70",
    isSection
      ? contactFormFieldInputClass
      : "rounded-md border border-border bg-background px-3 py-2 text-[0.8125rem]",
  );

  return (
    <div className={cn(reviewDensity ? "grid gap-1.5" : "grid gap-3", className)}>
      {!isSection ? (
        <span className="text-[0.75rem] font-medium text-muted">
          {t("contacts.groups.multiSelect.label")}
        </span>
      ) : null}

      {hasSelection ? (
        <div className="flex flex-wrap gap-1.5" aria-live="polite">
          {selectedExisting.map((group) => (
            <GroupChip
              key={`existing-${group.id}`}
              label={group.name}
              chipClassName={contactGroupChipClassName(group.id)}
              disabled={controlsDisabled}
              onRemove={() => removeExistingGroup(group.id)}
            />
          ))}
          {allowCreateNewGroup
            ? value.newGroupNames.map((name) => (
                <GroupChip
                  key={`new-${name}`}
                  label={name}
                  chipClassName={contactGroupChipClassNameFromKey(name)}
                  disabled={controlsDisabled}
                  onRemove={() => removeNewGroup(name)}
                  isPending
                />
              ))
            : null}
        </div>
      ) : compact && showEmptyHint ? (
        <p className="m-0 text-[0.75rem] leading-snug text-muted">
          {emptyHint ?? t("contacts.groups.multiSelect.emptyHint")}
        </p>
      ) : compact ? null : (
        <div
          className={cn(
            "rounded-lg border border-dashed px-3 py-2.5 text-start",
            isSection
              ? "border-border/60 bg-transparent px-0 py-0"
              : "border-transparent bg-transparent px-0 py-0",
          )}
        >
          <p className="m-0 text-[0.8125rem] font-medium text-foreground">
            {t("contacts.groups.multiSelect.empty")}
          </p>
          {isSection ? (
            <p className="m-0 mt-1 text-[0.75rem] leading-snug text-muted">
              {emptyHint ?? t("contacts.groups.multiSelect.emptyHint")}
            </p>
          ) : null}
        </div>
      )}

      {availableGroups.length > 0 ? (
        <div className={cn(isSection && "grid gap-1.5")}>
          {isSection && !compact ? (
            <span className="text-[0.75rem] font-medium text-muted">
              {existingLabelText}
            </span>
          ) : null}
          <Popover open={existingOpen} onOpenChange={setExistingOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={selectClassName}
                disabled={controlsDisabled}
                aria-label={existingTriggerText}
                data-testid="contact-group-existing-select"
              >
                <span className="min-w-0 flex-1 truncate text-muted">
                  {existingTriggerText}
                </span>
                <ChevronDown
                  size={reviewDensity ? 14 : 16}
                  className="shrink-0 opacity-60"
                  aria-hidden="true"
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              dir={dir}
              collisionPadding={12}
              className={cn(
                "min-w-56 max-w-[calc(100vw-1.5rem)] p-1",
                menuClassName ?? "w-[var(--radix-popover-trigger-width)]",
                "max-h-[min(15rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain",
              )}
              data-testid="contact-group-existing-menu"
            >
              <ul className="m-0 grid max-h-60 list-none gap-0.5 overflow-y-auto p-0">
                {availableGroups.map((group) => (
                  <li key={group.id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-start text-[0.8125rem] text-foreground hover:bg-surface-muted"
                      onClick={() => {
                        addExistingGroup(group.id);
                        setExistingOpen(false);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate">{group.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        </div>
      ) : null}

      {allowCreateNewGroup ? (
        <div
          data-testid="contact-group-create-section"
          className={cn(
            isSection &&
              "grid gap-2 rounded-lg border border-line/50 p-2.5",
          )}
        >
          {isSection ? (
            <span className="text-[0.75rem] font-medium text-muted">
              {createSectionLabelText}
            </span>
          ) : null}
          <div className="flex gap-2">
            <input
              type="text"
              className={createInputClassName}
              placeholder={createPlaceholderText}
              value={newGroupDraft}
              onChange={(event) => setNewGroupDraft(event.target.value)}
              maxLength={120}
              disabled={controlsDisabled}
              aria-label={createPlaceholderText}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addNewGroup();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1 px-3"
              disabled={controlsDisabled || newGroupDraft.trim().length === 0}
              onClick={addNewGroup}
            >
              <PlusIcon size={14} aria-hidden="true" />
              {t("contacts.groups.multiSelect.createAction")}
            </Button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="m-0 text-[0.75rem] text-muted">{t("contacts.groups.multiSelect.loading")}</p>
      ) : null}
    </div>
  );
}

function GroupChip({
  label,
  chipClassName,
  disabled,
  onRemove,
  isPending = false,
}: {
  label: string;
  chipClassName: string;
  disabled: boolean;
  onRemove: () => void;
  isPending?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Badge
      variant={isPending ? "outline" : "default"}
      className={cn("max-w-none gap-1 pe-1 ps-2 text-[0.75rem] font-medium", chipClassName)}
    >
      <span>{label}</span>
      <button
        type="button"
        className="inline-grid size-4 place-items-center rounded-full text-muted hover:bg-surface-muted hover:text-foreground disabled:opacity-50"
        onClick={onRemove}
        disabled={disabled}
        aria-label={t("contacts.groups.multiSelect.removeGroup", { name: label })}
      >
        <XIcon size={12} aria-hidden="true" />
      </button>
    </Badge>
  );
}
